<script setup lang="ts">
  import { onMounted, onUnmounted, ref } from "vue";
  import { useEventListener } from "@vueuse/core";
  import { storeToRefs } from "pinia";
  import OnePanePage from "@/templates/OnePanePage.vue";
  import { LoadingState } from "@/components/episto-ui/loading-state";
  import ContextSourcesPanel from "@/components/Survey/Context/ContextSourcesPanel.vue";
  import ContextEditor from "@/components/Survey/Context/ContextEditor.vue";
  import PasteTextDialog from "@/components/Survey/Context/PasteTextDialog.vue";
  import { useSurveyStore } from "@stores/survey/survey.js";
  import { useSurveyContextStore } from "@stores/survey/surveyContext";
  import { useToastsStore } from "@stores/toastsStore";
  import { useChannel } from "@utils/composables/useChannel";
  import { useUnsavedChangesGuard } from "@utils/composables/useUnsavedChangesGuard";

  const surveyToken = String(useSurveyStore().token);
  const surveyContextStore = useSurveyContextStore();
  const { context, hasUnsavedChanges } = storeToRefs(surveyContextStore);
  const toasts = useToastsStore();

  const uploading = ref(false);
  const pasteOpen = ref(false);
  const pasting = ref(false);
  const editedSource = ref<{ token: string; name: string; textContent: string } | null>(null);

  const run = async (action: () => Promise<void>, title: string) => {
    try {
      await action();
    } catch (error) {
      toasts.error(title, (error as Error).message);
    }
  };

  const addFiles = async (files: File[]) => {
    uploading.value = true;
    const failures = await surveyContextStore.addFiles(surveyToken, files);
    uploading.value = false;

    if (failures.length) toasts.error("Some files were not added", failures.join("\n"));
  };

  const openPaste = () => {
    editedSource.value = null;
    pasteOpen.value = true;
  };

  const openEdit = (token: string) =>
    run(async () => {
      const source = await surveyContextStore.fetchSource(surveyToken, token);
      editedSource.value = { token, name: source.name, textContent: source.text_content ?? "" };
      pasteOpen.value = true;
    }, "The text could not be opened");

  const saveText = async (name: string, textContent: string) => {
    pasting.value = true;
    const edited = editedSource.value;
    await run(
      async () => {
        if (edited) await surveyContextStore.updateText(surveyToken, edited.token, name, textContent);
        else await surveyContextStore.addText(surveyToken, name, textContent);
        pasteOpen.value = false;
      },
      edited ? "The text was not saved" : "The text was not added",
    );
    pasting.value = false;
  };

  const removeSource = (token: string) =>
    run(() => surveyContextStore.removeSource(surveyToken, token), "The source was not deleted");

  const generate = () => run(() => surveyContextStore.generate(surveyToken), "The generation did not start");

  const onGenerationStatus = async (message: { status: string }) => {
    if (message.status === "generating") {
      if (context.value?.status !== "generating")
        await run(() => surveyContextStore.fetch(surveyToken), "The context could not be reloaded");
      return;
    }

    await run(() => surveyContextStore.refreshIfGenerating(surveyToken), "The context could not be reloaded");
    if (message.status === "failed")
      toasts.error("The context was not generated", "Episto AI could not write it. Try again.");
  };

  useUnsavedChangesGuard(hasUnsavedChanges);
  useEventListener(window, "beforeunload", (event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges.value) event.preventDefault();
  });

  const channel = useChannel<{ status: string }>("SurveyContextChannel", { received: onGenerationStatus });

  onMounted(() => {
    channel.subscribe({ survey_token: surveyToken });
    run(() => surveyContextStore.fetch(surveyToken), "The context could not be loaded");
  });

  onUnmounted(() => {
    channel.unsubscribe();
    surveyContextStore.reset();
  });
</script>

<template>
  <OnePanePage>
    <div
      data-cy="survey-context-page"
      class="flex flex-col gap-6 rounded-xl md:h-[calc(100vh-60px-40px)] md:overflow-hidden border bg-card px-6 pt-6 pb-6 shadow-sm xl:px-8 xl:pb-8">
      <h1 class="heading-page shrink-0">Context</h1>

      <LoadingState v-if="!context" />
      <div
        v-else
        class="grid min-h-0 flex-1 grid-cols-1 items-stretch gap-4 xl:gap-6 md:grid-cols-[minmax(15rem,18rem)_1fr] xl:grid-cols-[minmax(18rem,24rem)_1fr] md:grid-rows-[minmax(0,1fr)]">
        <ContextSourcesPanel
          :sources="context.sources"
          :survey-token="surveyToken"
          :uploading="uploading"
          @add-files="addFiles"
          @paste-text="openPaste"
          @edit="openEdit"
          @remove="removeSource" />
        <ContextEditor
          :context="context"
          @generate="generate" />
      </div>

      <PasteTextDialog
        v-model:open="pasteOpen"
        :loading="pasting"
        :initial="editedSource"
        @save="saveText" />
    </div>
  </OnePanePage>
</template>
